// A mock draft's room, from its kit (MOCK_DRAFT.md §3 to §6.1, BACKLOG D16).
//
// WHAT ANY CLIENT NEEDS TO RUN A MOCK, beside the room itself: where the
// person's team goes and who sits everywhere else, the grades when it ends, and
// the record that keeps it. It lived in the web client until kept mocks made it
// a contract, and a second frontend running a mock would otherwise have had to
// work out the same seating and grading by itself.
//
// PURE. A host — the browser's, or a script's — builds the room from
// `mockPrepared`, keeps a clock of its own and hands it intents.

import type { DraftKit, SaveMockRequest } from '@fsp/domain';
import { draftRecap, type DraftRecap } from '@fsp/draft';
import { practiceAuction, type LiveDraftPrepared } from './prepared';
import { auctionValuesOf, type Room } from './room';

/** What a mock's setup asks. `run` makes each mock its own, even from the same seat. */
export interface MockSetup {
  /** 1-based, in round-one order. */
  slot: number;
  bots: 'quick' | 'instant';
  clock: 'none' | 'league';
  run: number;
}

/**
 * The room a mock runs on, from the kit and the setup.
 *
 * MY TEAM GOES WHERE I CHOSE, and the team that sat there takes my old seat:
 * every other seat is a bot, so which bot sits where is all a swap changes. A
 * member with no team of their own drafts the seat they chose.
 */
export function mockPrepared(
  kit: DraftKit,
  setup: MockSetup,
  userId: string,
  myName = 'You',
): { prepared: LiveDraftPrepared; myTeamId: string } {
  const order = [...kit.seats].sort((a, b) => a.slot - b.slot).map((s) => s.teamId);
  const k = Math.min(Math.max(setup.slot, 1), order.length) - 1;
  const myTeamId = kit.league.myTeamId ?? order[k]!;
  const at = order.indexOf(myTeamId);
  if (at >= 0 && at !== k) [order[at], order[k]] = [order[k]!, order[at]!];

  const seatOf = new Map(kit.seats.map((s) => [s.teamId, s]));
  const seats = order.map((teamId, i) => {
    const s = seatOf.get(teamId)!;
    const mine = teamId === myTeamId;
    return {
      teamId,
      slot: i + 1,
      // EACH SEAT IS CALLED BY WHO DRAFTS IT IN THE MOCK: me, or its bot —
      // not the leaguemate or the open seat it is in the real draft.
      team: { ...s.team, ownerName: mine ? myName : s.bot.manager.name },
      ownerUserId: mine ? userId : null,
      bot: mine ? null : s.bot,
    };
  });

  const { myPlan, ...house } = kit.context;
  return {
    myTeamId,
    prepared: {
      leagueId: kit.league.id,
      draftId: `mock:${kit.league.id}:${String(setup.run)}`,
      draftStatus: 'scheduled',
      league: { ...kit.league, myTeamId },
      mode: 'live',
      type: kit.type,
      // AN AUCTION, on an evening's clocks whatever the league's (AUCTION.md §6.4).
      auction: kit.auction === null ? null : practiceAuction(kit.auction),
      rounds: kit.rounds,
      pickSeconds: kit.pickSeconds,
      // NO CLOCK is the soft timer: the room waits for my pick at zero. A
      // league that does not draft live has no clock rule of its own, so its
      // practice clock picks at zero, as a live room's does by default.
      whenTheClockExpires:
        setup.clock === 'none'
          ? 'off'
          : kit.draftMode === 'live'
            ? kit.whenTheClockExpires
            : 'queue-then-rankings',
      scheduledAt: null,
      startedAt: null,
      order,
      orderFixed: true,
      seats,
      players: kit.players,
      basis: kit.basis,
      byeWeeks: kit.byeWeeks,
      context: {
        ...house,
        plans: myPlan === null ? {} : { [myTeamId]: myPlan },
        // EACH MOCK ITS OWN: the bots' choices are keyed on this.
        seed: `${kit.context.seed}:mock:${String(setup.run)}`,
      },
      slots: kit.slots,
      needs: kit.needs,
      commissionerIds: [userId],
      preparedAt: kit.preparedAt,
    },
  };
}

/**
 * The grades, as the real recap gives them, for a mock that has finished; null
 * before. Its lines name each team's drafter, so mine is named as I am, which
 * reads right wherever a line puts it ("a strong night for Casey").
 */
export function mockRecap(
  _kit: DraftKit,
  room: Room,
  myTeamId: string,
  myName = 'You',
): DraftRecap | null {
  const seatOf = new Map(room.prepared.seats.map((s) => [s.teamId, s]));
  return roomRecap(room, (teamId) =>
    teamId === myTeamId ? myName : (seatOf.get(teamId)?.bot?.manager.name ?? 'A bot'),
  );
}

/**
 * The grades of any finished room, from the board it drafted on — a solo mock
 * in the browser, or a league mock in the live worker (MOCK_DRAFT.md §6.2),
 * which has no kit. `ownerName` is who each line names.
 */
export function roomRecap(room: Room, ownerName: (teamId: string) => string): DraftRecap | null {
  const players = new Map(room.prepared.players.map((p) => [p.id, p]));
  const rankOf = new Map(room.prepared.context.rankings.map((id, i) => [id, i + 1]));
  const total = room.state.order.length * room.state.rounds;
  const seatOf = new Map(room.prepared.seats.map((s) => [s.teamId, s]));
  // AN AUCTION'S PRICES AGAINST THEIR VALUES, as the real recap judges them.
  const worth = auctionValuesOf(room.prepared);
  const rules = room.prepared.auction ?? null;
  return draftRecap(room.state, {
    ...(worth === null || rules === null
      ? {}
      : {
          auction: {
            worth: (id: string) => worth[id] ?? rules.minBid,
            margin: Math.max(3, Math.round(rules.budget / 40)),
          },
        }),
    value: (id) => players.get(id)?.value ?? 0,
    // Where the board expected him, clamped to the end of the draft, as the
    // real recap reads it (`draft-reads.ts`).
    adp: (id) => Math.min(rankOf.get(id) ?? total + 1, total + 1),
    position: (id) => room.prepared.context.positions[id] ?? '',
    playerName: (id) => players.get(id)?.summary.name ?? 'somebody',
    ownerName,
    teamName: (teamId) => seatOf.get(teamId)?.team.name ?? 'A team',
    // WHAT A BOT'S STYLE CAME TO, as the real recap says it (AI_MANAGERS.md §4.7).
    style: (teamId, ids) => room.styleRecapOf(teamId, ids),
  });
}

/**
 * The record that keeps a finished mock (MOCK_DRAFT.md §6.1), or null for one
 * that has not finished: what `POST /api/leagues/:id/draft/mocks` takes.
 * `key` is the run's own, so the same mock sent twice is kept once.
 */
export function savedMockOf(
  room: Room,
  setup: MockSetup,
  myTeamId: string,
  recap: DraftRecap | null,
  /** The record's key: the run's by default; a league mock's names the mock and the person. */
  key = `${room.prepared.leagueId}:${String(setup.run)}`,
): SaveMockRequest | null {
  if (room.state.status !== 'complete' || recap === null) return null;
  const seatOf = new Map(room.prepared.seats.map((s) => [s.teamId, s]));
  const order = [...room.state.order];
  const mine = recap.teams.find((t) => t.teamId === myTeamId) ?? null;
  return {
    key,
    slot: order.indexOf(myTeamId) + 1,
    bots: setup.bots,
    clock: setup.clock,
    order,
    // A SEAT IS CALLED BY WHO DREW IT: its bot, or, in a league mock, the
    // friend who sat in it.
    drafters: order.map((teamId) =>
      teamId === myTeamId
        ? null
        : (seatOf.get(teamId)?.bot?.manager.name ?? seatOf.get(teamId)?.team.ownerName ?? 'A bot'),
    ),
    // AND WHICH OF THEM WERE PEOPLE: a league mock's other seats somebody sat in.
    people: order.filter((teamId) => {
      const seat = seatOf.get(teamId);
      return (
        teamId !== myTeamId && seat !== undefined && seat.ownerUserId !== null && seat.bot === null
      );
    }),
    picks: [...room.state.picks]
      .sort((a, b) => a.overall - b.overall)
      // An auction's squares, each with its price: every team's buys in order.
      .map((p) => ({
        playerId: p.playerId,
        by: p.by,
        ...(p.price === undefined ? {} : { price: p.price }),
      })),
    grades: recap.teams.map((t) => ({ teamId: t.teamId, rank: t.rank, grade: t.grade })),
    line: mine?.line ?? '',
    best: mine?.bestPick?.overall ?? null,
    headline: recap.headline,
  };
}
